import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  CircularProgress,
  Box,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History'; // Icono para revertir

// Helper para formatear fechas
const formatDate = (dateString) => {
  if (!dateString) return 'Fecha desconocida';
  try {
    return new Date(dateString).toLocaleString(undefined, { 
      dateStyle: 'medium', 
      timeStyle: 'short' 
    });
  } catch (e) {
    return dateString; // Devolver original si falla el parseo
  }
};

function PromptHistoryModal({ open, onClose, agentId, historyData = [], onRevert, isLoading }) {
  const [revertingId, setRevertingId] = useState(null); // ID del historial que se está revirtiendo
  const [revertError, setRevertError] = useState(null);

  const handleRevertClick = async (historyId) => {
    setRevertingId(historyId);
    setRevertError(null);
    try {
      await onRevert(historyId);
      // El éxito lo maneja el componente padre (cerrar modal, refrescar)
    } catch (error) {
        console.error("Error al revertir prompt:", error);
        setRevertError(`Error al revertir: ${error.message || 'Error desconocido'}`);
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md" // Hacerlo un poco más ancho para prompts largos
      aria-labelledby="prompt-history-dialog-title"
    >
      <DialogTitle id="prompt-history-dialog-title">
        Historial de Prompts para: {agentId}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {revertError && <Alert severity="error" sx={{ mb: 2 }}>{revertError}</Alert>}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando historial...</Typography>
          </Box>
        ) : historyData.length === 0 ? (
          <Typography sx={{ textAlign: 'center', p: 3 }}>
            No hay historial de prompts disponible para este agente.
          </Typography>
        ) : (
          <List>
            {historyData.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={`Versión guardada el: ${formatDate(item.created_at)}`}
                    secondary={
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          mt: 1, 
                          p: 1.5, 
                          bgcolor: 'grey.50', 
                          whiteSpace: 'pre-wrap', // Conservar saltos de línea y espacios
                          wordBreak: 'break-word', // Romper palabras largas si es necesario
                          fontFamily: 'monospace', // Usar fuente monoespaciada
                          fontSize: '0.85rem',
                          maxHeight: '200px', // Limitar altura y permitir scroll interno
                          overflowY: 'auto' 
                        }}
                      >
                        {item.previous_prompt}
                      </Paper>
                    }
                  />
                  <Box sx={{ ml: 2, mt: 1, display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={revertingId === item.id ? <CircularProgress size={16} /> : <HistoryIcon />}
                      onClick={() => handleRevertClick(item.id)}
                      disabled={revertingId !== null} // Deshabilitar si ya se está revirtiendo otra versión
                    >
                      {revertingId === item.id ? 'Revirtiendo...' : 'Revertir a esta versión'}
                    </Button>
                  </Box>
                </ListItem>
                {/* No añadir Divider si es el último elemento */}
                {index < historyData.length - 1 && <Divider component="li" />} 
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default PromptHistoryModal; 